import { AnimatePresence, motion, useInView, useReducedMotion } from "framer-motion";
import { ArrowLeft, BadgeCheck, Pause, Play } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import cn from "~/utils/cn";
import { formatNumber } from "../../plans";
import Button from "../../ui/Button";
import type { LandingData } from "../types";
import { faceStyle, type Face } from "./faces";

const FACES: Face[] = [
  ["peyda", 900],
  ["modam", 300],
  ["yekan", 800],
  ["peyda", 500],
  ["modam", 850],
  ["yekan", 200],
];
const EASE = [0.16, 1, 0.3, 1] as const;
// CSS entrance (keyframes live in LandingB) so the hero shows before and without hydration: headline, text, CTA, then the specimen.
const enter = "motion-safe:animate-[b-rise_.9s_cubic-bezier(.16,1,.3,1)_backwards]";

export default function HeroB({ data }: { data: LandingData }) {
  const { t } = useTranslation();
  const pro = data.proDaysLeft !== null;
  const title = t("ui.landing.hero.title");
  const emphasis = t("ui.landing.hero.titleEmphasis");
  const at = title.indexOf(emphasis);

  return (
    // from 900px the hero fills the first screen, so the big outlined wordmark below starts under the fold instead of peeking in as stray shapes
    <section className="mx-auto grid w-full max-w-[1200px] items-center gap-9 min-[900px]:min-h-[calc(100dvh-var(--nav-h))] px-[var(--gutter)] pb-[clamp(36px,5vw,64px)] pt-[clamp(20px,5vw,64px)] min-[900px]:grid-cols-[6fr_5fr] min-[900px]:gap-[clamp(40px,5vw,72px)]">
      <div>
        {pro && (
          <p className={cn(enter, "mb-4 flex items-center gap-2 font-display text-[15px] font-bold text-success")}>
            <BadgeCheck className="size-5 shrink-0" strokeWidth={1.75} aria-hidden />
            {t("ui.landing.hero.proUntil", { days: formatNumber(data.proDaysLeft!) })}
          </p>
        )}
        <h1 className={cn(enter, "text-balance font-display text-[clamp(2rem,1rem+3.4vw,4rem)] font-black leading-[1.3] text-ink")}>
          {at < 0 ? (
            title
          ) : (
            <>
              {title.slice(0, at)}
              <em className="not-italic text-accent-text">{emphasis}</em>
              {title.slice(at + emphasis.length)}
            </>
          )}
        </h1>
        <p className={cn(enter, "mt-[18px] max-w-[42ch] text-pretty text-[clamp(1rem,.94rem+.3vw,1.18rem)] leading-[1.9] text-ink-2 [animation-delay:90ms]")}>
          {t("ui.landing.hero.subtitle")}
        </p>
        <Button
          id="hero-cta"
          href="#plans"
          size="lg"
          className={cn(
            enter,
            "group mt-[30px] shadow-[inset_0_1px_0_rgb(255_255_255/.18),0_10px_24px_-12px_rgb(53_107_206/.7)] [animation-delay:180ms]",
          )}
        >
          {pro ? t("ui.landing.hero.ctaRenew") : t("ui.landing.hero.cta")}
          <ArrowLeft className="size-[18px] transition-transform duration-300 ease-out group-hover:-translate-x-1 ltr:rotate-180" strokeWidth={1.75} aria-hidden />
        </Button>
      </div>
      <Specimen />
    </section>
  );
}

// One sentence through every family and weight Pro unlocks. Pauses offscreen, in a hidden tab, on request, and starts paused with reduced motion.
function Specimen() {
  const { t } = useTranslation();
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref, { amount: 0.4 });
  const reduce = useReducedMotion();
  const [k, setK] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (reduce) setPlaying(false);
  }, [reduce]);
  useEffect(() => {
    const sync = () => setHidden(document.hidden);
    document.addEventListener("visibilitychange", sync);
    return () => document.removeEventListener("visibilitychange", sync);
  }, []);
  useEffect(() => {
    if (!playing || !inView || hidden) return;
    const id = setInterval(() => setK((i) => (i + 1) % FACES.length), 2600);
    return () => clearInterval(id);
  }, [playing, inView, hidden]);

  const face = FACES[k];
  const fade = reduce ? { duration: 0 } : { duration: 0.3, ease: EASE };

  return (
    <figure
      ref={ref}
      className={cn(
        enter,
        "m-0 flex min-h-[clamp(250px,34vw,440px)] flex-col gap-[18px] rounded-lg border border-line bg-surface p-[clamp(20px,2.6vw,32px)] shadow-card [animation-delay:300ms]",
      )}
    >
      <figcaption className="sr-only">{t("ui.landing.b.specimen.label")}</figcaption>
      <div className="flex items-center gap-2.5 text-sm text-ink-3">
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={k}
            aria-hidden
            className="flex items-baseline gap-2.5"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={reduce ? { duration: 0 } : { duration: 0.25 }}
          >
            <span className="font-display text-[15px] font-bold text-ink">{t(`ui.landing.b.fonts.${face[0]}`)}</span>
            {t("ui.landing.b.weight", { weight: formatNumber(face[1]) })}
          </motion.span>
        </AnimatePresence>
        <button
          type="button"
          onClick={() => setPlaying((p) => !p)}
          aria-label={playing ? t("ui.landing.b.specimen.pause") : t("ui.landing.b.specimen.play")}
          className="-me-2.5 ms-auto grid size-11 place-items-center rounded-full text-ink-3 transition-colors duration-200 hover:bg-accent-soft hover:text-ink"
        >
          {playing ? <Pause className="size-[18px]" strokeWidth={1.75} aria-hidden /> : <Play className="size-[18px]" strokeWidth={1.75} aria-hidden />}
        </button>
      </div>
      <div aria-hidden className="grid min-h-[calc(2*1.35em)] flex-1 text-[clamp(2.6rem,1.5rem+4vw,4.5rem)]">
        <AnimatePresence mode="wait" initial={false}>
          <motion.p
            key={k}
            style={faceStyle(face)}
            className="col-start-1 row-start-1 self-center text-balance leading-[1.35] text-ink"
            initial={{ opacity: 0, y: "0.2em" }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: "-0.2em" }}
            transition={fade}
          >
            {t("ui.landing.b.specimen.sentence")}
          </motion.p>
        </AnimatePresence>
      </div>
      <div aria-hidden className="flex items-baseline justify-between gap-4 border-t border-line pt-3.5 text-xl leading-[1.6] text-ink-2" style={faceStyle(face)}>
        <span>{t("ui.landing.b.specimen.alphabet")}</span>
        <span>۱۲۳۴۵</span>
      </div>
    </figure>
  );
}
